import { LinkOutlined, WarningOutlined } from "@mui-symbols-material/w300"
import { useNavigate } from "@tanstack/react-router"
import type { FC } from "react"
import React from "react"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import CircularLoadingIndicator from "@/components/common/LoadingIndicators/CircularLoadingIndicator"
import { useGetAssociatedLocations } from "@/screens/Companies/hooks/useGetAssociatedLocations"
import { PaymentTypeDetailsSection } from "@/screens/CompanySettings/Accounting/components/PaymentTypeDetails/PaymentTypeDetailsSection"
import { usePaymentTermsContext } from "@/screens/CompanySettings/Accounting/contexts/PaymentTermsContext"

const PaymentSettingsContainer: FC<{ companyId: string }> = ({ companyId }) => {
  const navigate = useNavigate()

  const { isCompanyVendor, isCompanyCustomer } = usePaymentTermsContext()
  const { associatedLocationList, associatedLocationListFetching } = useGetAssociatedLocations({ companyId })

  const noCustomerOrVendor = !isCompanyCustomer && !isCompanyVendor
  const hasBillingLocation = associatedLocationList.some((location) => location.billing?.locationId)

  const handleAddBillingLocationClick = () => {
    navigate({ to: `/app/companies/${companyId}/edit/locations` })
  }

  if (noCustomerOrVendor) {
    return (
      <InformationMissingAlert
        title='Accounting information is not used for Government or Akrochem company types. Please review the company type if you believe this is an error.'
        className='mb-4 mt-2 bg-tertiary-yellow-100 px-6 pb-2 pt-4 text-tertiary-yellow-900'
        titleClassName='text-ml'
        icon={<WarningOutlined className='p-0 text-tertiary-yellow-900' />}
        description=''
      />
    )
  }

  if (associatedLocationListFetching) {
    return <CircularLoadingIndicator />
  }

  return !hasBillingLocation ? (
    <InformationMissingAlert
      title='There is no Company Billing Location set up for this company'
      className='mb-4 mt-2 bg-tertiary-yellow-100 px-6 pb-2 pt-4 text-tertiary-yellow-900'
      titleClassName='text-ml'
      icon={<WarningOutlined className='p-0 text-tertiary-yellow-900' />}
      description=''
      action={
        <Button
          appearance='outlined'
          size='small'
          className='border-tertiary-yellow-900 text-primary'
          color='inherit'
          startIcon={<LinkOutlined />}
          onClick={handleAddBillingLocationClick}
        >
          Associate Location
        </Button>
      }
    />
  ) : (
    <PaymentTypeDetailsSection companyId={companyId} />
  )
}

export default PaymentSettingsContainer
